// SCR-STAFF-600 — اسناد مراجعه‌کننده (تب «اسناد» پرونده؛ US-DOC-001، 003 تا 006، 060، 061): فهرست با فیلتر نوع، متن و بازه تاریخ و
// Cursor؛ بارگذاری (فایل یا عکس مستقیم دوربین تبلت/موبایل — AC-DOC-001.3)، نسخه تازه، پیش‌نمایش داخلی با واترمارک، دانلود (Permission جدا و
// Audit)، ابطال با دلیل و بخش «ابطال‌شده‌ها»، اشتراک امن پیامکی و تاریخچه دسترسی، بررسی یکپارچگی Hash.
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ScannerOutlinedIcon from '@mui/icons-material/ScannerOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DocCaptureStationView,
  type DocDocumentListItem,
  type DocDocumentTypeView,
  type DocDocumentView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { docKeys, fileSize, num, saveDocBlob, SCR_STAFF_600 } from './common';
import { DocumentViewer, fetchFile } from './DocumentViewer';

const SENSITIVITY_TONE: Record<string, 'default' | 'warning' | 'error'> = {
  General: 'default',
  Clinical: 'warning',
  HighlySensitive: 'error',
};

export function useDocumentTypes() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: docKeys.types(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/document-types')),
    staleTime: 60_000,
  });
}

/** ایستگاه‌های دارای اسکنر یا پد امضا (AP-INT-05)؛ انتخاب پیش‌فرض نخستین ایستگاه آنلاین است. */
export function useCaptureStations(enabled = true) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: docKeys.captureStations(session?.tenantId),
    enabled,
    staleTime: 30_000,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/capture-stations', { params: { query: {} } })),
  });
}

export function useTypeLabel() {
  const { i18n } = useTranslation('doc');
  const types = useDocumentTypes();
  return (code: string) => {
    const type = types.data?.items.find((item) => item.code === code);
    return type ? (i18n.language === 'en' && type.nameEn ? type.nameEn : type.name) : code;
  };
}

function UploadDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const types = useDocumentTypes();
  const idempotency = useIdempotencyKey();
  const fileId = useId();
  const cameraId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [typeCode, setTypeCode] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [station, setStation] = useState('');
  const [feeder, setFeeder] = useState(true);
  const [scanReason, setScanReason] = useState<string | null>(null);
  const selectable = (types.data?.items ?? []).filter((type: DocDocumentTypeView) => type.isActive && type.isSelectable);
  const stations = useCaptureStations();
  const scanners = (stations.data?.items ?? []).filter((row: DocCaptureStationView) => row.hasScanner);
  const chosen = scanners.some((row) => row.workstationId === station)
    ? station
    : (scanners.find((row) => row.isOnline) ?? scanners[0])?.workstationId ?? '';

  // AC-DOC-011.1: اسکن مستقیم از اسکنر ایستگاه؛ هیچ فایلی روی سیستم محلی نمی‌ماند و شکست عامل سند نیم‌کاره نمی‌سازد.
  const scan = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/documents:scan', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            workstationId: chosen,
            typeCode,
            patientId,
            title: title || null,
            feeder,
            duplex: false,
            color: false,
            dpi: 200,
            maxPages: 20,
          },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error),
    onSuccess: (result) => {
      if (!result.scanned) {
        setScanReason(result.errorReason ?? 'unknown');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });

  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file as File, (file as File).name);
      form.append('typeCode', typeCode);
      form.append('patientId', patientId);
      if (documentDate) {
        form.append('documentDate', documentDate);
      }
      if (title) {
        form.append('title', title);
      }
      if (description) {
        form.append('description', description);
      }
      return unwrap(
        tenantApi.POST('/api/v1/documents/documents', {
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="doc:documents.upload_title"
      screenId={SCR_STAFF_600}
      onSubmit={() => file && typeCode && upload.mutate()}
      onClose={onClose}
      isDirty={file !== null}
      isSubmitting={upload.isPending || scan.isPending}
      submitError={upload.error ?? scan.error}
    >
      <Stack direction="row" spacing={1}>
        <Button component="label" htmlFor={fileId} variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
          {t('doc:documents.choose_file')}
        </Button>
        <input
          id={fileId}
          type="file"
          hidden
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.docx,.xlsx"
          data-testid="doc-upload-file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        {/* AC-DOC-001.3: عکس مستقیم از مدرک با دوربین پشتی تبلت/موبایل (برش لبه‌ها با دوربین سیستم‌عامل). */}
        <Button component="label" htmlFor={cameraId} variant="outlined" startIcon={<PhotoCameraOutlinedIcon />} data-testid="doc-upload-camera-button">
          {t('doc:documents.take_photo')}
        </Button>
        <input
          id={cameraId}
          type="file"
          hidden
          accept="image/*"
          capture="environment"
          data-testid="doc-upload-camera"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </Stack>
      {file ? (
        <Typography data-testid="doc-upload-selected">
          {file.name} · {fileSize(file.size)}
        </Typography>
      ) : null}
      {scanners.length > 0 ? (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              select
              fullWidth
              label={t('doc:documents.scan_station')}
              value={chosen}
              onChange={(event) => setStation(event.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'doc-scan-station' } }}
            >
              {scanners.map((row) => (
                <MenuItem key={row.workstationId} value={row.workstationId}>
                  {row.name}
                  {row.isOnline ? '' : ` — ${t('doc:documents.scan_offline')}`}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<ScannerOutlinedIcon />}
              disabled={!chosen || !typeCode || scan.isPending}
              onClick={() => {
                setScanReason(null);
                scan.mutate();
              }}
              data-testid="doc-scan"
            >
              {t('doc:documents.scan')}
            </Button>
          </Stack>
          <FormControlLabel
            control={<Switch checked={feeder} onChange={(event) => setFeeder(event.target.checked)} data-testid="doc-scan-feeder" />}
            label={t('doc:documents.scan_feeder')}
          />
          {scanReason ? (
            <Alert severity="warning" data-testid="doc-scan-failed">
              {t(`doc:scan_reason.${scanReason}`, { defaultValue: t('doc:documents.scan_failed') })}
            </Alert>
          ) : null}
        </Stack>
      ) : null}
      <TextField
        select
        label={t('doc:documents.type')}
        value={typeCode}
        onChange={(event) => setTypeCode(event.target.value)}
        slotProps={{ htmlInput: { 'data-testid': 'doc-upload-type' } }}
        required
      >
        {selectable.map((type) => (
          <MenuItem key={type.code} value={type.code}>
            {type.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="date"
        label={t('doc:documents.document_date')}
        value={documentDate}
        onChange={(event) => setDocumentDate(event.target.value)}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'doc-upload-date' } }}
      />
      <TextField label={t('doc:documents.title_field')} value={title} onChange={(event) => setTitle(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'doc-upload-title', maxLength: 200 } }} />
      <TextField
        label={t('doc:documents.description')}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        multiline
        minRows={2}
        slotProps={{ htmlInput: { 'data-testid': 'doc-upload-description', maxLength: 1000 } }}
      />
    </FormDialog>
  );
}

function VersionDialog({ document, onClose }: { document: DocDocumentListItem; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const inputId = useId();
  const [file, setFile] = useState<File | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file as File, (file as File).name);
      return unwrap(
        tenantApi.POST('/api/v1/documents/documents/{id}/versions', {
          params: { path: { id: document.id } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      titleKey="doc:documents.version_title"
      screenId={SCR_STAFF_600}
      onSubmit={() => file && save.mutate()}
      onClose={onClose}
      isDirty={file !== null}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <Typography>{t('doc:documents.version_hint', { version: faDigits(num(document.currentVersion) + 1) })}</Typography>
      <Button component="label" htmlFor={inputId} variant="outlined" sx={{ alignSelf: 'flex-start' }}>
        {t('doc:documents.choose_file')}
      </Button>
      <input id={inputId} type="file" hidden data-testid="doc-version-file" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
      {file ? <Typography>{file.name}</Typography> : null}
    </FormDialog>
  );
}

function VoidDialog({ document, onClose }: { document: DocDocumentListItem; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const run = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/documents/documents/{id}:void', {
          params: { path: { id: document.id } },
          body: { reason: reason as 'WrongPatient', note: note || null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      titleKey="doc:documents.void_title"
      screenId={SCR_STAFF_600}
      onSubmit={() => reason && run.mutate()}
      onClose={onClose}
      isDirty={reason !== ''}
      isSubmitting={run.isPending}
      submitError={run.error}
    >
      <Alert severity="info">{t('doc:documents.void_hint')}</Alert>
      <TextField select label={t('doc:documents.void_reason')} value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'doc-void-reason' } }} required>
        {['WrongPatient', 'Duplicate', 'Illegible', 'Other'].map((value) => (
          <MenuItem key={value} value={value}>
            {t(`doc:void_reason.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField label={t('doc:documents.void_note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 500 } }} />
    </FormDialog>
  );
}

function DetailDialog({ documentId, onClose }: { documentId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [sharing, setSharing] = useState(false);
  const detail = useQuery({
    queryKey: docKeys.document(session?.tenantId, documentId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/documents/{id}', { params: { path: { id: documentId } } })),
  });
  const shares = useQuery({
    queryKey: docKeys.shares(session?.tenantId, documentId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/documents/{id}/shares', { params: { path: { id: documentId } } })),
    enabled: access.can('doc.document.share'),
  });
  const integrity = useMutation({
    mutationFn: () => unwrap(tenantApi.GET('/api/v1/documents/documents/{id}/integrity', { params: { path: { id: documentId } } })),
  });
  const share = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/documents/documents/{id}:share', { params: { path: { id: documentId } }, body: { channel: 'Sms', patientId: null } })),
    onSuccess: () => {
      setSharing(false);
      void queryClient.invalidateQueries({ queryKey: docKeys.shares(session?.tenantId, documentId) });
    },
  });

  const view: DocDocumentView | undefined = detail.data;
  return (
    <FormDialog open titleKey="doc:documents.detail_title" screenId={SCR_STAFF_600} onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="doc:actions.close" maxWidth="md">
      {detail.isPending ? <LoadingState rows={3} /> : null}
      {detail.error ? <ErrorState error={detail.error} /> : null}
      {view ? (
        <Stack spacing={2} data-testid="doc-detail">
          <Typography variant="subtitle1">{view.document.title ?? view.document.fileName}</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            SHA-256: <bdi dir="ltr" data-testid="doc-detail-hash">{view.sha256}</bdi>
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button variant="outlined" onClick={() => integrity.mutate()} data-testid="doc-integrity-check" disabled={integrity.isPending}>
              {t('doc:documents.integrity_check')}
            </Button>
            {integrity.data ? (
              <Chip
                color={integrity.data.intact ? 'success' : 'error'}
                label={t(integrity.data.intact ? 'doc:documents.integrity_ok' : 'doc:documents.integrity_failed')}
                data-testid="doc-integrity-result"
                data-intact={integrity.data.intact ? 'true' : 'false'}
              />
            ) : null}
          </Stack>
          {integrity.error ? <ErrorState error={integrity.error} compact /> : null}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2">{t('doc:documents.versions')}</Typography>
            <List dense data-testid="doc-versions">
              {view.versions.map((version) => (
                <ListItem key={String(version.number)}>
                  <ListItemText
                    primary={t('doc:documents.version_line', { version: faDigits(num(version.number)), file: version.fileName })}
                    secondary={
                      <>
                        {version.uploadedByName ?? '—'} · <JalaliDate value={version.uploadedAt} withTime />
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          {access.can('doc.document.share') ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">{t('doc:share.history')}</Typography>
                <Button onClick={() => setSharing(true)} disabled={view.document.sensitivity === 'HighlySensitive' || view.document.status !== 'Active'} data-testid="doc-share">
                  {t('doc:share.action')}
                </Button>
              </Stack>
              {view.document.sensitivity === 'HighlySensitive' ? <Alert severity="info">{t('doc:share.highly_sensitive')}</Alert> : null}
              {share.error ? <ErrorState error={share.error} compact /> : null}
              {share.data ? (
                <Alert severity="success" data-testid="doc-share-done">
                  {t('doc:share.done')}
                </Alert>
              ) : null}
              <List dense data-testid="doc-share-history">
                {(shares.data?.items ?? []).map((item) => (
                  <ListItem key={item.id} data-testid={`doc-share-${item.id}`}>
                    <ListItemText
                      primary={`${item.sharedByName ?? '—'} → ${item.recipientName ?? '—'} · ${t(`doc:share.channel.${item.channel}`)} · ${t(`doc:share.status.${item.status}`, { defaultValue: item.status })}`}
                      secondary={item.events.map((event) => t(`doc:share.event.${event.event}`, { defaultValue: event.event })).join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : null}
          <ConfirmDialog
            open={sharing}
            titleKey="doc:share.confirm_title"
            messageKey="doc:share.confirm"
            confirmKey="doc:share.action"
            busy={share.isPending}
            onConfirm={() => share.mutate()}
            onCancel={() => setSharing(false)}
          />
        </Stack>
      ) : null}
    </FormDialog>
  );
}

export function PatientDocumentsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const typeLabel = useTypeLabel();
  const types = useDocumentTypes();
  const [typeCode, setTypeCode] = useState('');
  const [text, setText] = useState('');
  const [voided, setVoided] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<DocDocumentListItem | null>(null);
  const [versioning, setVersioning] = useState<DocDocumentListItem | null>(null);
  const [voiding, setVoiding] = useState<DocDocumentListItem | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const filter = `${typeCode}|${text}|${voided ? '1' : '0'}`;

  const list = useInfiniteQuery({
    queryKey: docKeys.documents(session?.tenantId, patientId, filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/documents/documents', {
          params: {
            query: {
              patientId,
              typeCode: typeCode || undefined,
              q: text || undefined,
              voided: voided || undefined,
              cursor: pageParam ?? undefined,
              limit: 25,
            },
          },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? null,
  });

  const download = useMutation({
    mutationFn: async (row: DocDocumentListItem) => {
      const url = await unwrap(tenantApi.POST('/api/v1/documents/documents/{id}:download-url', { params: { path: { id: row.id }, query: {} } }));
      saveDocBlob(await fetchFile(tenantApi, url.url), row.fileName);
    },
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const canUpload = access.can('doc.document.upload');
  const columns: DataTableColumn<DocDocumentListItem>[] = [
    {
      id: 'type',
      headerKey: 'doc:documents.columns.type',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{typeLabel(row.typeCode)}</span>
          {row.isSigned ? <Chip size="small" color="success" variant="outlined" label={t('doc:documents.signed')} /> : null}
        </Stack>
      ),
    },
    {
      id: 'title',
      headerKey: 'doc:documents.columns.title',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{row.title ?? row.fileName}</span>
          {row.description ? (
            <Typography variant="caption" color="text.secondary">
              {row.description}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    { id: 'date', headerKey: 'doc:documents.columns.date', cell: (row) => (row.documentDate ? <JalaliDate value={row.documentDate} /> : <JalaliDate value={row.createdAt} />) },
    { id: 'uploader', headerKey: 'doc:documents.columns.uploader', cell: (row) => row.uploadedByName ?? '—' },
    {
      id: 'sensitivity',
      headerKey: 'doc:documents.columns.sensitivity',
      cell: (row) => (
        <Chip size="small" color={SENSITIVITY_TONE[row.sensitivity] ?? 'default'} label={t(`doc:sensitivity.${row.sensitivity}`)} data-testid={`doc-sensitivity-${row.id}`} />
      ),
    },
    { id: 'version', headerKey: 'doc:documents.columns.version', cell: (row) => `${faDigits(num(row.currentVersion))} · ${fileSize(row.size)}` },
    {
      id: 'actions',
      headerKey: 'doc:documents.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          <Button size="small" onClick={() => setViewing(row)} data-testid={`doc-preview-${row.id}`}>
            {t('doc:actions.preview')}
          </Button>
          {access.can('doc.document.download') ? (
            <Button size="small" onClick={() => download.mutate(row)} data-testid={`doc-download-${row.id}`}>
              {t('doc:actions.download')}
            </Button>
          ) : null}
          <Button size="small" onClick={() => setDetail(row.id)} data-testid={`doc-detail-${row.id}`}>
            {t('doc:actions.details')}
          </Button>
          {canUpload && !row.isSigned && row.status === 'Active' ? (
            <Button size="small" onClick={() => setVersioning(row)} data-testid={`doc-version-${row.id}`}>
              {t('doc:actions.new_version')}
            </Button>
          ) : null}
          {access.can('doc.document.void') && row.status === 'Active' ? (
            <Button size="small" color="warning" onClick={() => setVoiding(row)} data-testid={`doc-void-${row.id}`}>
              {t('doc:actions.void')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_600} data-testid="patient-documents">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' } }}>
        <TextField
          select
          size="small"
          label={t('doc:documents.type')}
          value={typeCode}
          onChange={(event) => setTypeCode(event.target.value)}
          sx={{ minWidth: 200 }}
          slotProps={{ htmlInput: { 'data-testid': 'doc-filter-type' } }}
        >
          <MenuItem value="">{t('doc:documents.all_types')}</MenuItem>
          {(types.data?.items ?? []).map((type) => (
            <MenuItem key={type.code} value={type.code}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label={t('doc:documents.search')} value={text} onChange={(event) => setText(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'doc-filter-text' } }} />
        {access.can('doc.document.void') ? (
          <FormControlLabel control={<Switch checked={voided} onChange={(event) => setVoided(event.target.checked)} slotProps={{ input: { 'data-testid': 'doc-filter-voided' } as never }} />} label={t('doc:documents.voided')} />
        ) : null}
        <span style={{ flexGrow: 1 }} />
        {canUpload ? (
          <Button variant="contained" startIcon={<CloudUploadOutlinedIcon />} onClick={() => setUploading(true)} data-testid="doc-upload">
            {t('doc:documents.upload')}
          </Button>
        ) : null}
      </Stack>
      {download.error ? <ErrorState error={download.error} compact /> : null}
      {list.isPending ? <LoadingState rows={4} /> : null}
      {list.error ? <ErrorState error={list.error} onRetry={() => void list.refetch()} /> : null}
      {list.data && rows.length === 0 ? <EmptyState titleKey={voided ? 'doc:documents.empty_voided' : 'doc:documents.empty'} /> : null}
      {rows.length > 0 ? (
        <DataTable
          captionKey="doc:documents.title"
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          hasMore={list.hasNextPage}
          onLoadMore={() => void list.fetchNextPage()}
          isFetchingMore={list.isFetchingNextPage}
        />
      ) : null}
      {uploading ? <UploadDialog patientId={patientId} onClose={() => setUploading(false)} /> : null}
      {viewing ? <DocumentViewer documentId={viewing.id} title={viewing.title ?? viewing.fileName} onClose={() => setViewing(null)} /> : null}
      {versioning ? <VersionDialog document={versioning} onClose={() => setVersioning(null)} /> : null}
      {voiding ? <VoidDialog document={voiding} onClose={() => setVoiding(null)} /> : null}
      {detail ? <DetailDialog documentId={detail} onClose={() => setDetail(null)} /> : null}
    </Stack>
  );
}
